import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { GlobalStyles } from './styles/GlobalStyles';
import ProductConfigurator from './components/ProductConfigurator';
import QuotePreview from './components/QuotePreview';
import PoleConfigurator from './components/PoleConfigurator';
import ShippingCalculator from './components/ShippingCalculator';
import YoutubePage from './components/YoutubePage';
import LandingPage from './components/LandingPage';
import ContactPage from './components/ContactPage';
import AdminDashboard from './components/admin/AdminDashboard';
import useConfiguratorStore from './store/configuratorStore';
import { isAdminEmail } from './utils/adminWhitelist';
import { useAuth } from './hooks/useAuth';

const AppWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  overflow: hidden;
  background-color: white;
`;

const Sidebar = styled.aside`
  width: 230px;
  background-color: var(--secondary-color);
  background-image: linear-gradient(135deg, var(--secondary-color) 0%, #003366 100%);
  color: white;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 0 18px 18px 0;
  margin-left: -10px;
  padding-left: 10px;
  box-shadow: 5px 0 25px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease-in-out;

  @media (max-width: 1024px) {
    transform: ${props => props.$isMobileOpen ? 'translateX(0)' : 'translateX(-100%)'};
    margin-left: 0;
    padding-left: 0;
    border-radius: 0;
    box-shadow: ${props => props.$isMobileOpen ? '5px 0 25px rgba(0, 0, 0, 0.3)' : 'none'};

    > * {
      padding-left: 10px;
      padding-right: 10px;
    }
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 50%, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0) 70%);
    pointer-events: none;
  }
`;

const Logo = styled.div`
  padding: 1.1rem 1rem 0.9rem;
  margin-top: 0.4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-bottom: 0.5rem;
`;

const LogoIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  background: transparent;
  border-radius: 12px;

  img {
    height: 24px;
    width: auto;
    object-fit: contain;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
    transition: all 0.3s ease;
  }
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  padding: 0.25rem 0 0;
  flex: 1;
  position: relative;
  overflow: visible;
`;

const NavGroup = styled.div`
  margin-bottom: 0.75rem;
`;

const NavGroupTitle = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 9.5px;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 0 1.2rem 0.2rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 600;
  position: relative;
  z-index: 2;
  opacity: 0.9;
  transition: opacity 0.2s ease-in-out;
`;

const NavIcon = styled.span`
  font-size: 1.1em;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$active ? 1 : 0.9};
  flex-shrink: 0;
`;

const NavLink = styled.a`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  color: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.82)'};
  font-size: 12px;
  font-weight: ${props => props.$active ? '600' : '500'};
  padding: 0.45rem 1.2rem;
  margin: 0.1rem 0.6rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.8rem;
  position: relative;
  z-index: 2;
  cursor: pointer;
  transform: ${props => props.$active ? 'translateX(2px)' : 'none'};
  background-color: ${props => props.$active ? 'rgba(255, 255, 255, 0.08)' : 'transparent'};

  &:hover {
    color: white;
    background-color: rgba(255, 255, 255, 0.08);
    transform: translateX(2px);
  }

  @media (max-width: 1024px) {
    padding: 0.75rem 1.2rem;
    min-height: 48px;
  }
`;

const MainContainer = styled.div`
  flex: 1;
  margin-left: 230px;
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  @media (max-width: 1024px) {
    margin-left: 0;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  position: fixed;
  top: 15px;
  left: 15px;
  z-index: 1002;
  background: var(--secondary-color);
  color: white;
  border: none;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 24px;
  line-height: 1;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  transition: all 0.3s ease;
  min-width: 48px;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 1024px) {
    display: flex;
  }

  @media (min-width: 1025px) {
    display: none;
  }
`;

const Overlay = styled(motion.div)`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;

  @media (max-width: 1024px) {
    display: ${props => props.$show ? 'block' : 'none'};
  }
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  background-color: white;
`;

const ConfiguratorContainer = styled.div`
  flex: 1;
  display: flex;
  height: 100vh;
  overflow: hidden;
  position: relative;

  @media (max-width: 1024px) {
    flex-direction: column;
    height: auto;
    overflow-y: auto;
  }
`;

const CategoryNavigation = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  background-color: white;
  z-index: 10;
  border-bottom: 1px solid var(--gray-200);
  padding: 0 1rem;
  display: flex;
  justify-content: flex-start;
  box-shadow: var(--shadow-sm);
  border-radius: 0;
  height: 64px;

  @media (max-width: 768px) {
    height: 56px;
    padding: 0 0.75rem;
  }

  @media (max-width: 640px) {
    height: 52px;
    padding: 0 0.5rem;
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  max-width: 100%;
  width: 100%;
  overflow-x: auto;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const CategoryTab = styled.button`
  padding: 1.25rem 1.5rem;
  border-bottom: 3px solid ${props => props.active ? 'var(--primary-color)' : 'transparent'};
  color: ${props => props.active ? 'var(--primary-color)' : 'var(--secondary-color)'};
  font-weight: ${props => props.active ? '600' : '500'};
  font-size: var(--text-md);
  transition: var(--transition-fast);
  white-space: nowrap;
  display: flex;
  align-items: center;
  min-height: 44px;

  &:hover {
    border-color: ${props => props.active ? 'var(--primary-color)' : 'var(--primary-light)'};
    color: ${props => props.active ? 'var(--primary-color)' : 'var(--primary-color)'};
    background-color: ${props => props.active ? 'transparent' : 'var(--primary-light)'};
  }

  @media (max-width: 768px) {
    padding: 1rem 1.25rem;
    font-size: 0.9rem;
  }

  @media (max-width: 640px) {
    padding: 0.875rem 1rem;
    font-size: 0.85rem;
  }
`;

const CategoryTabIcon = styled.span`
  margin-right: 0.5rem;
  font-size: 1.25em;
  color: ${props => props.active ? 'var(--primary-color)' : 'var(--secondary-color)'};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;

  img {
    height: 24px;
    width: auto;
    object-fit: contain;
    transition: all 0.2s ease;
  }

  @media (max-width: 640px) {
    width: 1.25rem;
    height: 1.25rem;
    margin-right: 0.375rem;

    img {
      height: 20px;
    }
  }
`;


const FullPageContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(135deg, #f5f8fb 0%, #ffffff 100%);
  min-height: 100vh;
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 2rem;
`;

const ModalContent = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 1200px;
  aspect-ratio: 16 / 9;
  background: black;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const CloseButton = styled.button`
  position: absolute;
  top: -40px;
  right: 0;
  background: white;
  color: var(--secondary-color);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: var(--primary-color);
    color: white;
    transform: scale(1.1);
  }
`;

const IframeWrapper = styled.div`
  width: 100%;
  height: 100%;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const BrochureCard = styled(motion.div)`
  position: relative;
  width: clamp(70%, 78vw, 880px);
  max-width: 920px;
  height: clamp(420px, 65vh, 620px);
  max-height: 640px;
  border-radius: 32px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.18);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, #f5f8fb 0%, #e8ecf1 100%);
  padding: clamp(1.75rem, 5vw, 3.25rem);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-12px) scale(1.01);
    box-shadow: 0 26px 72px rgba(15, 23, 42, 0.22);
  }

  img {
    width: clamp(180px, 42%, 360px);
    max-height: 100%;
    height: auto;
    object-fit: contain;
    filter: drop-shadow(0 20px 44px rgba(15, 23, 42, 0.18));
    transition: transform 0.4s ease, filter 0.4s ease;
  }

  &:hover img {
    transform: scale(1.04);
    filter: drop-shadow(0 36px 64px rgba(15, 23, 42, 0.24));
  }

  @media (max-width: 1024px) {
    width: 95%;
    max-width: 760px;
    height: clamp(380px, 62vh, 560px);
    max-height: 580px;
  }

  @media (max-width: 768px) {
    width: 95%;
    height: 60vh;
    border-radius: 24px;
    padding: clamp(1.5rem, 6vw, 2.5rem);

    img {
      width: clamp(160px, 64%, 320px);
    }
  }
`;

const BrochureOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(4px);

  ${BrochureCard}:hover & {
    opacity: 1;
  }
`;

const OpenBrochureButton = styled.button`
  padding: 1rem 2.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 8px 24px rgba(255, 143, 0, 0.4);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(255, 143, 0, 0.5);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const DocumentGrid = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(260px, 1fr));
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(260px, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const DocumentCard = styled(motion.div)`
  position: relative;
  width: 100%;
  background: linear-gradient(135deg, #f7f9fc 0%, #eef1f7 100%);
  border-radius: 18px;
  padding: 1.5rem;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.24);
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-8px) scale(1.01);
    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.16);
  }

  @media (max-width: 640px) {
    padding: 1.25rem;
  }
`;

const DocumentImage = styled.img`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  object-fit: contain;
  padding: 6px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: white;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.1);
  flex-shrink: 0;
`;

const DocumentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
`;

const DocumentTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--secondary-color);
  margin: 0;
  line-height: 1.3;
`;

const DocumentDescription = styled.p`
  margin: 0;
  color: var(--gray-600);
  line-height: 1.55;
  font-size: 0.875rem;
`;

const DocumentMeta = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  color: var(--gray-500);
  font-size: 0.775rem;
  margin-top: 0.25rem;
`;

const DocumentActionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 0.65rem;
  margin-top: 0.25rem;
`;

const DocumentButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  border-radius: 9px;
  background: linear-gradient(135deg, #1d3a60 0%, #142845 100%);
  color: white;
  font-weight: 600;
  font-size: 0.8rem;
  text-decoration: none;
  text-align: center;
  white-space: nowrap;
  box-shadow: 0 8px 18px rgba(20, 40, 69, 0.2);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  svg {
    flex-shrink: 0;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 26px rgba(20, 40, 69, 0.24);
  }
`;

const PdfModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2001;
  padding: 2rem;
  backdrop-filter: blur(8px);

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PdfModalContent = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 1400px;
  height: 90vh;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    height: 85vh;
    border-radius: 12px;
  }
`;

const PdfModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, var(--secondary-color) 0%, #003366 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const PdfModalTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
`;

const PdfCloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const PdfViewer = styled.iframe`
  flex: 1;
  width: 100%;
  height: 100%;
  border: none;
  background: white;
`;

function App() {
  const [hasEnteredEmail, setHasEnteredEmail] = useState(false);
  const [currentView, setCurrentView] = useState('configurator');
  const [isSmallViewport, setIsSmallViewport] = useState(window.innerWidth <= 1024);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [modalVideoId, setModalVideoId] = useState(null);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const { setMainCategory, selectedMainCategory, mainCategories } = useConfiguratorStore();
  const { session } = useAuth();
  const technicalDocuments = [
    {
      image: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/springhead.avif',
      id: 'springhead-drawing',
      title: 'Neva Springhead Technical Drawing',
      description: 'High-resolution technical drawing of the Springhead assembly with callouts and reference dimensions for engineering teams.',
      url: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/Technical%20Docs/Neva%20technical%20drawing%20Springhead.pdf',
      meta: ['Format: PDF', 'Version: 2025']
    },
    {
      image: 'https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/decks.images/Deck%20Standard.avif',
      id: 'deck-standard-drawing',
      title: 'Neva Deck | Standard Technical Drawing',
      description: 'Detailed deck layout showing the standard configuration, mounting points, and safety clearances for installation teams.',
      url: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/Technical%20Docs/Neva%20technical%20drawing%20Springhead.pdf',
      meta: ['Format: PDF', 'Version: 2025']
    },
    {
      image: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/flow.avif',
      id: 'flow-pole-drawings',
      title: 'Neva Flow Pole Technical Drawings',
      description: 'Separate technical drawings for each Flow Pole length with structural notes and mounting dimensions.',
      downloads: [
        {
          label: 'Small | 1180mm',
          url: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/Technical%20Docs/Flow%20Pole%20Galv.%20Steel%20602,5mm%201180mm%20%20Small.pdf'
        },
        {
          label: 'Medium | 1730mm',
          url: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/Technical%20Docs/Flow%20Pole%20Galv.%20Steel%20602,5mm%201730mm%20%20Medium.pdf'
        },
        {
          label: 'Large | 2130mm',
          url: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/Technical%20Docs/Flow%20Pole%20Galv.%20Steel%20602,5mm%202130mm%20%20Large.pdf'
        }
      ],
      meta: ['Format: PDF', 'Version: 2025']
    },
    {
      image: 'https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/unloadingstation/Frame-Base.avif',
      id: 'unloading-station-drawings',
      title: 'Modular Unloading Station Technical Drawings',
      description: 'Height-specific layouts for the modular unloading station including safety clearances, mounting interfaces, and service zones.',
      downloads: [
        {
          label: 'Small | 880-1580mm',
          url: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/Technical%20Docs/Modular%20unloading%20station%20-%20Feet%20set%20Small,%20WH%20880-1580mm.pdf'
        },
        {
          label: 'Medium | 1680-2380mm',
          url: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/Technical%20Docs/Modular%20unloading%20station%20-%20Feet%20set%20Medium,%20%20WH%201680-2380mm.pdf'
        },
        {
          label: 'Large | 2480-3180mm',
          url: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/Technical%20Docs/Modular%20unloading%20station%20-%20%20Feet%20set%20Large%20%20WH%202480-3180mm.pdf'
        }
      ],
      meta: ['Format: PDF', 'Version: 2025']
    }
  ];

  useEffect(() => {
    // Check for stored email OR active session
    const storedEmail = localStorage.getItem('indus_user_email');

    // If user has email in localStorage OR has an active session, grant access
    if (storedEmail || session) {
      setHasEnteredEmail(true);
      setCurrentView('configurator');
      if (!selectedMainCategory) {
        setMainCategory('poles');
      }
    }
  }, [session]); // Also depend on session changes

  useEffect(() => {
    const handleResize = () => {
      const small = window.innerWidth <= 1024;
      setIsSmallViewport(small);
      if (!small) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleEmailSubmit = (email) => {
    localStorage.setItem('indus_user_email', email);
    setHasEnteredEmail(true);
    setCurrentView('configurator');
    if (!selectedMainCategory) {
      setMainCategory('poles');
    }
  };

  const handleOpenVideoModal = (videoId) => {
    setModalVideoId(videoId);
  };

  const handleCloseVideoModal = () => {
    setModalVideoId(null);
  };

  const renderContent = () => {
    switch (currentView) {
      case 'ourVision':
        return (
          <FullPageContainer>
            <BrochureCard
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              onClick={() => setIsPdfModalOpen(true)}
            >
              <img
                src="https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/folder.webp"
                alt="Indus Brochure"
              />
              <BrochureOverlay>
                <OpenBrochureButton onClick={(e) => {
                  e.stopPropagation();
                  setIsPdfModalOpen(true);
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Open Brochure
                </OpenBrochureButton>
              </BrochureOverlay>
            </BrochureCard>
          </FullPageContainer>
        );

      case 'configurator':
        return (
          <ConfiguratorContainer>
            <CategoryNavigation>
              <CategoryTabs>
                {mainCategories.map(category => (
                  <CategoryTab
                    key={category.id}
                    active={selectedMainCategory === category.id}
                    onClick={() => setMainCategory(category.id)}
                  >
                    <CategoryTabIcon active={selectedMainCategory === category.id}>
                      {category.id === 'poles' ? (
                        <img
                          src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/poles/Poles%20Section.avif"
                          alt="Lockhead Pole"
                          style={{
                            filter: selectedMainCategory === category.id ? 'brightness(1)' : 'brightness(0.8) opacity(0.85)',
                            transform: selectedMainCategory === category.id ? 'scale(1.1)' : 'scale(1)'
                          }}
                        />
                      ) : category.id === 'decks' ? (
                        <img
                          src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/decks.images/Deck%20Standard.avif"
                          alt="Standard Deck"
                          style={{
                            filter: selectedMainCategory === category.id ? 'brightness(1)' : 'brightness(0.8) opacity(0.85)',
                            transform: selectedMainCategory === category.id ? 'scale(1.1)' : 'scale(1)'
                          }}
                        />
                      ) : category.id === 'unloading_stations' ? (
                        <img
                          src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/unloadingstation/Unloading%20Station.avif"
                          alt="Unloading Station"
                          style={{
                            filter: selectedMainCategory === category.id ? 'brightness(1)' : 'brightness(0.8) opacity(0.85)',
                            transform: selectedMainCategory === category.id ? 'scale(1.1)' : 'scale(1)'
                          }}
                        />
                      ) : (
                        category.icon
                      )}
                    </CategoryTabIcon>
                    {category.name}
                  </CategoryTab>
                ))}
              </CategoryTabs>
            </CategoryNavigation>
            <ProductConfigurator />
            {selectedMainCategory !== 'meet_neva' && <QuotePreview />}
          </ConfiguratorContainer>
        );

      case 'poleCalculator':
        return (
          <FullPageContainer>
            <PoleConfigurator />
          </FullPageContainer>
        );

      case 'shippingCalculator':
        return (
          <FullPageContainer>
            <ShippingCalculator />
          </FullPageContainer>
        );

      case 'youtube':
        return (
          <FullPageContainer style={{
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            padding: '2rem',
            background: 'white'
          }}>
            <YoutubePage onOpenVideoModal={handleOpenVideoModal} />
          </FullPageContainer>
        );

      case 'docs':
        return (
          <FullPageContainer style={{
            alignItems: 'flex-start',
            background: 'linear-gradient(135deg, #f5f8fb 0%, #ffffff 100%)',
            padding: '2.5rem 6%',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <DocumentGrid>
              {technicalDocuments.map((doc, index) => (
                <DocumentCard
                  key={doc.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: 'easeOut', delay: index * 0.05 }}
                >
                  <DocumentHeader>
                    <DocumentImage src={doc.image} alt={doc.title} />
                    <DocumentTitle>{doc.title}</DocumentTitle>
                  </DocumentHeader>
                  <DocumentDescription>{doc.description}</DocumentDescription>
                  <DocumentMeta>
                    {doc.meta.map((item, metaIndex) => (
                      <span key={metaIndex}>{item}</span>
                    ))}
                  </DocumentMeta>
                  <DocumentActionRow>
                    {doc.downloads ? (
                      doc.downloads.map((file, downloadIndex) => (
                        <DocumentButton
                          key={downloadIndex}
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          aria-label={`Download ${file.label}`}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                          {file.label}
                        </DocumentButton>
                      ))
                    ) : (
                      <DocumentButton
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        aria-label={`Download ${doc.title}`}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Download PDF
                      </DocumentButton>
                    )}
                  </DocumentActionRow>
                </DocumentCard>
              ))}
            </DocumentGrid>
          </FullPageContainer>
        );

      case 'contact':
        return (
          <FullPageContainer style={{
            background: 'linear-gradient(135deg, #f5f8fb 0%, #ffffff 100%)',
            alignItems: 'flex-start',
            padding: '2.5rem 3%'
          }}>
            <ContactPage />
          </FullPageContainer>
        );

      case 'admin':
        return (
          <FullPageContainer style={{
            background: 'linear-gradient(135deg, #f5f8fb 0%, #ffffff 100%)',
            alignItems: 'flex-start',
            padding: '2.5rem 3%'
          }}>
            <AdminDashboard />
          </FullPageContainer>
        );

      default:
        return (
          <FullPageContainer>
            <div style={{ textAlign: 'center', maxWidth: '600px' }}>
              <h1 style={{ fontSize: '2.5rem', marginBottom: '1rem', color: 'var(--secondary-color)' }}>
                Welcome to Indus Quote Builder
              </h1>
              <p style={{ fontSize: '1.2rem', color: 'var(--gray-600)', marginBottom: '2rem' }}>
                Configure and price your Indus products with our interactive quote builder.
              </p>
              <button
                onClick={() => {
                  setCurrentView('configurator');
                  if (!selectedMainCategory) {
                    setMainCategory('poles');
                  }
                }}
                style={{
                  background: 'linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '1rem 2rem',
                  borderRadius: '12px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(255, 143, 0, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(255, 143, 0, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(255, 143, 0, 0.3)';
                }}
              >
                Start Building Quote
              </button>
            </div>
          </FullPageContainer>
        );
    }
  };

  if (!hasEnteredEmail) {
    return (
      <>
        <GlobalStyles />
        <LandingPage onEmailSubmit={handleEmailSubmit} />
      </>
    );
  }

  return (
    <>
      <GlobalStyles />
      <AppWrapper>
        {isSmallViewport && (
          <HamburgerButton onClick={() => setIsSidebarOpen(prev => !prev)}>
            {isSidebarOpen ? <>×</> : <>☰</>}
          </HamburgerButton>
        )}
        <Overlay $show={isSmallViewport && isSidebarOpen} onClick={() => setIsSidebarOpen(false)} />

        <Sidebar $isMobileOpen={isSidebarOpen}>
          <Logo>
            <LogoIcon>
              <img src="https://i.imgur.com/63cuLGv.png" alt="Indus Logo" />
            </LogoIcon>
          </Logo>

          <Nav>
            <NavGroup>
              <NavGroupTitle>Products</NavGroupTitle>
              <NavLink
                href="#"
                $active={currentView === 'configurator'}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentView('configurator');
                  if (!selectedMainCategory) {
                    setMainCategory('poles');
                  }
                  if (isSmallViewport) setIsSidebarOpen(false);
                }}
              >
                <NavIcon $active={currentView === 'configurator'}>
                  <img
                    src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/decks.images//android-chrome-512x512.png"
                    alt="Configure"
                    style={{
                      width: '18px',
                      height: '18px',
                      objectFit: 'contain',
                      opacity: currentView === 'configurator' ? 1 : 0.85
                    }}
                  />
                </NavIcon>
                <span>Configure Now</span>
              </NavLink>
            </NavGroup>

            <NavGroup>
              <NavGroupTitle>Resources</NavGroupTitle>
              <NavLink
                href="#"
                $active={currentView === 'ourVision'}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentView('ourVision');
                  if (isSmallViewport) setIsSidebarOpen(false);
                }}
              >
                <NavIcon $active={currentView === 'ourVision'}>📙</NavIcon>
                <span>Our Brochure</span>
              </NavLink>
              <NavLink
                href="#"
                $active={currentView === 'youtube'}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentView('youtube');
                  if (isSmallViewport) setIsSidebarOpen(false);
                }}
              >
                <NavIcon $active={currentView === 'youtube'}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="6" width="20" height="12" rx="3" fill="#FF0000" />
                    <path d="M15 12L10 15V9L15 12Z" fill="white" />
                  </svg>
                </NavIcon>
                <span>Videos</span>
              </NavLink>
              <NavLink
                href="#"
                $active={currentView === 'docs'}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentView('docs');
                  if (isSmallViewport) setIsSidebarOpen(false);
                }}
              >
                <NavIcon $active={currentView === 'docs'}>📑</NavIcon>
                <span>Technical Docs</span>
              </NavLink>
            </NavGroup>

            <NavGroup>
              <NavGroupTitle>Contact</NavGroupTitle>
              <NavLink
                href="#"
                $active={currentView === 'contact'}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentView('contact');
                  if (isSmallViewport) setIsSidebarOpen(false);
                }}
              >
                <NavIcon $active={currentView === 'contact'}>💬</NavIcon>
                <span>Our Team</span>
              </NavLink>
            </NavGroup>

            {/* Admin Dashboard - Only visible to authenticated admins */}
            {session && isAdminEmail(session.user?.email) && (
              <NavGroup>
                <NavGroupTitle>Admin</NavGroupTitle>
                <NavLink
                  href="#"
                  $active={currentView === 'admin'}
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentView('admin');
                    if (isSmallViewport) setIsSidebarOpen(false);
                  }}
                >
                  <NavIcon $active={currentView === 'admin'}>🔧</NavIcon>
                  <span>Admin Dashboard</span>
                </NavLink>
              </NavGroup>
            )}
          </Nav>
        </Sidebar>

        <MainContainer>
          <ContentArea>
            {renderContent()}
          </ContentArea>
        </MainContainer>

        <AnimatePresence>
          {modalVideoId && (
            <ModalOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCloseVideoModal}
            >
              <ModalContent
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: 'spring', damping: 15, stiffness: 200 }}
                onClick={(e) => e.stopPropagation()}
              >
                <CloseButton onClick={handleCloseVideoModal}>&times;</CloseButton>
                <IframeWrapper>
                  <iframe
                    src={`https://www.youtube.com/embed/${modalVideoId}?autoplay=1`}
                    title="YouTube video player"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </IframeWrapper>
              </ModalContent>
            </ModalOverlay>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {isPdfModalOpen && (
            <PdfModalOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsPdfModalOpen(false)}
            >
              <PdfModalContent
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                onClick={(e) => e.stopPropagation()}
              >
                <PdfModalHeader>
                  <PdfModalTitle>Indus Plastics Brochure 2025-2026</PdfModalTitle>
                  <PdfCloseButton onClick={() => setIsPdfModalOpen(false)}>
                    &times;
                  </PdfCloseButton>
                </PdfModalHeader>
                <PdfViewer
                  src="https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/Files/Indus%20Plastics%20Brochure%202025-2026.pdf"
                  title="Indus Plastics Brochure"
                />
              </PdfModalContent>
            </PdfModalOverlay>
          )}
        </AnimatePresence>
      </AppWrapper>
    </>
  );
}

export default App;
